<template>
    <mobile-template title="Mon compte">

        <template #header-left>
            <div>
                <p class="text-xl font-bold text-gray-900">Mon compte</p>
                <p class="text-xs text-gray-400">Gérez votre profil</p>
            </div>
        </template>

        <!-- ══ BARRE SOMBRE ══════════════════════════════════════════ -->
        <div class="bg-gray-950">
            <div class="px-4 pt-4 pb-5">

                <!-- Loading state dans le header -->
                <div v-if="loading" class="flex items-center gap-4">
                    <div class="w-14 h-14 rounded-2xl bg-white/10 animate-pulse shrink-0"></div>
                    <div class="space-y-2">
                        <div class="h-5 w-32 bg-white/10 rounded-lg animate-pulse"></div>
                        <div class="h-3 w-24 bg-white/10 rounded-lg animate-pulse"></div>
                    </div>
                </div>

                <!-- Avatar + nom -->
                <div v-else class="flex items-center gap-4">
                    <div class="w-14 h-14 rounded-2xl bg-orange-500 flex items-center justify-center shrink-0">
                        <span class="text-xl font-black text-white uppercase">
                            {{ (form.prenom?.[0] ?? '') }}{{ (form.nom?.[0] ?? '') }}
                        </span>
                    </div>
                    <div>
                        <p class="text-lg font-extrabold text-white leading-tight capitalize">
                            {{ form.prenom }} {{ form.nom }}
                        </p>
                        <p class="text-xs text-white/40 mt-0.5">{{ form.email }}</p>
                        <p v-if="societe" class="text-xs text-orange-400 font-semibold mt-0.5">{{ societe.nom }}</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- ══ LOADING ════════════════════════════════════════════ -->
        <div v-if="loading" class="flex flex-col items-center justify-center py-20 gap-3">
            <div class="w-8 h-8 border-2 border-gray-200 border-t-orange-500 rounded-full animate-spin"></div>
            <p class="text-sm text-gray-400">Chargement…</p>
        </div>

        <template v-else>

            <!-- ══ ALERTES ════════════════════════════════════════ -->
            <div v-if="isMandatory" class="mx-4 mt-4 flex items-start gap-3 bg-red-50 border border-red-200 rounded-2xl p-4">
                <svg class="w-5 h-5 text-red-500 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                <p class="text-sm text-red-700 font-semibold leading-snug">
                    Veuillez ajouter votre signature pour accéder aux interventions.
                </p>
            </div>

            <transition name="fade">
                <div v-if="successMessage" class="mx-4 mt-4 flex items-center gap-3 bg-green-50 border border-green-200 rounded-2xl p-4">
                    <svg class="w-5 h-5 text-green-600 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5l-4-4 1.41-1.41L10 13.67l6.59-6.59L18 8.5l-8 8z"/>
                    </svg>
                    <p class="text-sm text-green-700 font-semibold">{{ successMessage }}</p>
                </div>
            </transition>

            <transition name="fade">
                <div v-if="errorMessage" class="mx-4 mt-4 flex items-center gap-3 bg-red-50 border border-red-200 rounded-2xl p-4">
                    <svg class="w-5 h-5 text-red-500 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 13h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                    </svg>
                    <p class="text-sm text-red-700 font-semibold">{{ errorMessage }}</p>
                </div>
            </transition>

            <!-- ══ FORMULAIRE ════════════════════════════════════ -->
            <div class="p-4 space-y-3 pb-36">

                <!-- Section Informations personnelles -->
                <div class="bg-white rounded-2xl shadow-sm overflow-hidden">
                    <div class="px-4 pt-4 pb-1">
                        <p class="text-[10px] font-black text-slate-400 tracking-widest uppercase mb-3">Informations personnelles</p>
                    </div>

                    <!-- Nom -->
                    <div class="px-4 pb-3">
                        <label class="text-xs font-bold text-slate-500 mb-1.5 block">Nom *</label>
                        <div class="flex items-center gap-2.5 bg-slate-50 border border-slate-200 rounded-xl px-3 focus-within:border-orange-400 focus-within:ring-2 focus-within:ring-orange-100 transition">
                            <svg class="w-4 h-4 text-slate-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                            </svg>
                            <input
                                v-model="form.nom"
                                type="text"
                                class="flex-1 py-3 text-sm text-slate-800 bg-transparent outline-none placeholder-slate-400"
                                placeholder="Ex : Dupont"
                                :disabled="saving"
                            />
                        </div>
                    </div>

                    <!-- Prénom -->
                    <div class="px-4 pb-3">
                        <label class="text-xs font-bold text-slate-500 mb-1.5 block">Prénom *</label>
                        <div class="flex items-center gap-2.5 bg-slate-50 border border-slate-200 rounded-xl px-3 focus-within:border-orange-400 focus-within:ring-2 focus-within:ring-orange-100 transition">
                            <svg class="w-4 h-4 text-slate-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                            </svg>
                            <input
                                v-model="form.prenom"
                                type="text"
                                class="flex-1 py-3 text-sm text-slate-800 bg-transparent outline-none placeholder-slate-400"
                                placeholder="Ex : Jean"
                                :disabled="saving"
                            />
                        </div>
                    </div>

                    <!-- Email -->
                    <div class="px-4 pb-4">
                        <label class="text-xs font-bold text-slate-500 mb-1.5 block">Email *</label>
                        <div class="flex items-center gap-2.5 bg-slate-50 border border-slate-200 rounded-xl px-3 focus-within:border-orange-400 focus-within:ring-2 focus-within:ring-orange-100 transition">
                            <svg class="w-4 h-4 text-slate-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                            </svg>
                            <input
                                v-model="form.email"
                                type="email"
                                class="flex-1 py-3 text-sm text-slate-800 bg-transparent outline-none placeholder-slate-400"
                                placeholder="jean.dupont@exemple.fr"
                                autocomplete="email"
                                :disabled="saving"
                            />
                        </div>
                    </div>
                </div>

                <!-- Société (lecture seule) -->
                <div v-if="societe" class="bg-white rounded-2xl shadow-sm p-4 flex items-center gap-3">
                    <div class="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                        <svg class="w-4 h-4 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="2" y="7" width="20" height="14" rx="2"/>
                            <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
                        </svg>
                    </div>
                    <div>
                        <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Société</p>
                        <p class="text-sm font-bold text-slate-800 mt-0.5">{{ societe.nom }}</p>
                    </div>
                    <span class="ml-auto text-xs font-semibold text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full">Lecture seule</span>
                </div>

                <!-- Signature -->
                <div class="bg-white rounded-2xl shadow-sm overflow-hidden">
                    <div class="px-4 pt-4 pb-3">
                        <p class="text-[10px] font-black text-slate-400 tracking-widest uppercase mb-3">Signature</p>

                        <!-- Signature existante -->
                        <div v-if="form.signature" class="rounded-xl overflow-hidden border border-slate-200">
                            <div class="bg-slate-50 flex items-center justify-center p-4" style="min-height: 110px;">
                                <img :src="form.signature" alt="Signature" class="max-h-24 object-contain"/>
                            </div>
                            <button
                                class="w-full py-3 text-sm font-bold text-orange-600 bg-orange-50 hover:bg-orange-100 transition-colors flex items-center justify-center gap-2"
                                @click="openSignaturePad"
                            >
                                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                Modifier la signature
                            </button>
                        </div>

                        <!-- Pas de signature -->
                        <button
                            v-else
                            class="w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-orange-300 hover:border-orange-400 rounded-xl py-8 text-orange-500 transition-colors active:bg-orange-50"
                            @click="openSignaturePad"
                        >
                            <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            <span class="text-sm font-bold">Ajouter votre signature</span>
                            <span class="text-xs text-orange-400">Requise pour valider les interventions</span>
                        </button>
                    </div>
                </div>

                <!-- Déconnexion -->
                <div class="bg-white rounded-2xl shadow-sm overflow-hidden">
                    <button
                        class="w-full flex items-center justify-between px-4 py-3.5 active:bg-slate-50 transition-colors"
                        @click="displayLogout = !displayLogout"
                    >
                        <div class="flex items-center gap-3">
                            <div class="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center">
                                <svg class="w-4 h-4 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <path d="M12 16v-4M12 8h.01" stroke-linecap="round"/>
                                </svg>
                            </div>
                            <span class="text-sm font-bold text-slate-700">Informations supplémentaires</span>
                        </div>
                        <svg
                            class="w-4 h-4 text-slate-400 transition-transform"
                            :class="displayLogout ? 'rotate-180' : ''"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                        >
                            <path d="M19 9l-7 7-7-7" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>

                    <transition name="dropdown">
                        <div v-if="displayLogout" class="border-t border-slate-100 px-4 py-3">
                            <button
                                class="w-full flex items-center justify-center gap-2 py-3.5 bg-red-50 border border-red-200 text-red-600 rounded-xl font-bold text-sm active:bg-red-100 transition-colors"
                                @click="logout"
                            >
                                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                Se déconnecter
                            </button>
                        </div>
                    </transition>
                </div>

            </div>

            <!-- ══ FOOTER SAVE ════════════════════════════════════ -->
            <div class="fixed bottom-[100px] left-0 right-0 px-4 z-10">
                <button
                    class="w-full flex items-center justify-center gap-2 py-4 bg-orange-500 hover:bg-orange-600 text-white rounded-2xl font-extrabold text-base transition-colors disabled:opacity-50 active:scale-98"
                    :disabled="saving"
                    @click="handleSave"
                >
                    <span v-if="saving" class="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin"/>
                    <template v-else>
                        <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                            <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        Enregistrer les modifications
                    </template>
                </button>
            </div>

        </template>

        <!-- ══ MODAL SIGNATURE ════════════════════════════════════ -->
        <transition name="modal">
            <div v-if="showSignaturePad" class="fixed inset-0 z-50 bg-white flex flex-col">

                <!-- Header -->
                <div class="bg-gray-950 px-4 pt-12 pb-4 flex items-center justify-between">
                    <div>
                        <p class="text-lg font-extrabold text-white">Votre signature</p>
                        <p class="text-xs text-white/40 mt-0.5">Signez dans le cadre ci-dessous</p>
                    </div>
                    <button
                        class="w-9 h-9 flex items-center justify-center rounded-xl bg-white/10 active:bg-white/20"
                        @click="showSignaturePad = false"
                    >
                        <svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round"/>
                        </svg>
                    </button>
                </div>

                <!-- Canvas -->
                <div class="flex-1 flex flex-col items-center justify-center p-6 gap-4 bg-slate-50">
                    <div class="w-full max-w-sm bg-white rounded-2xl border-2 border-dashed border-slate-300 overflow-hidden shadow-sm">
                        <canvas
                            ref="signatureCanvas"
                            class="w-full touch-none block"
                            style="height: 200px;"
                            @mousedown="startDraw"
                            @mousemove="draw"
                            @mouseup="stopDraw"
                            @mouseleave="stopDraw"
                            @touchstart.prevent="startDrawTouch"
                            @touchmove.prevent="drawTouch"
                            @touchend="stopDraw"
                        />
                    </div>
                    <p class="text-sm text-slate-400 text-center">Utilisez votre doigt ou un stylet pour signer</p>
                </div>

                <!-- Actions -->
                <div class="flex gap-3 px-4 pt-3 pb-8 bg-white border-t border-slate-200">
                    <button
                        class="flex-1 py-3.5 border-2 border-slate-200 rounded-2xl text-slate-600 font-bold text-sm active:bg-slate-50 transition-colors"
                        @click="clearCanvas"
                    >
                        Effacer
                    </button>
                    <button
                        class="flex-1 py-3.5 bg-orange-500 hover:bg-orange-600 text-white rounded-2xl font-extrabold text-sm flex items-center justify-center gap-2 disabled:opacity-50 active:scale-98 transition-all"
                        :disabled="uploadingSignature"
                        @click="saveSignature"
                    >
                        <span v-if="uploadingSignature" class="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"/>
                        <template v-else>
                            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            Valider la signature
                        </template>
                    </button>
                </div>

            </div>
        </transition>

    </mobile-template>
</template>

<script>
import axios from 'axios';

export default {
    name: 'ProfilView',

    data() {
        return {
            loading:            true,
            saving:             false,
            uploadingSignature: false,
            showSignaturePad:   false,
            profile:            null,
            displayLogout:      false,
            form: {
                nom:       '',
                prenom:    '',
                email:     '',
                signature: null,
            },
            societe:        null,
            successMessage: '',
            errorMessage:   '',
            _drawing:       false,
            _ctx:           null,
        };
    },

    computed: {
        isMandatory() {
            return this.$route?.query?.mandatory === '1';
        },
    },

    created() {
        this.loadProfile();
    },

    methods: {
        async loadProfile() {
            try {
                this.loading = true;
                const data    = await this.$request.get('/auth/me', {});
                const account = data?.account || data;
                this.profile         = account;
                this.form.nom        = account.nom     || '';
                this.form.prenom     = account.prenom  || '';
                this.form.email      = account.email   || '';
                this.form.signature  = account.signature || null;
                this.societe         = account.societe || null;
            } catch (error) {
                this.errorMessage = error?.message || 'Impossible de charger le profil';
            } finally {
                this.loading = false;
            }
        },

        async handleSave() {
            this.errorMessage = '';
            if (!this.form.nom.trim() || !this.form.prenom.trim() || !this.form.email.trim()) {
                this.errorMessage = 'Veuillez remplir tous les champs obligatoires.';
                return;
            }
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(this.form.email)) {
                this.errorMessage = 'Veuillez entrer une adresse email valide.';
                return;
            }
            if (!this.profile?.id) {
                this.errorMessage = 'Profil introuvable.';
                return;
            }
            try {
                this.saving = true;
                await this.$request.post(`/techniciens/update/${this.profile.id}`, {
                    nom:       this.form.nom,
                    prenom:    this.form.prenom,
                    email:     this.form.email,
                    signature: this.form.signature,
                });
                this.$toast.success('Profil mis à jour avec succès');
                setTimeout(() => {
                    this.successMessage = '';
                    if (this.isMandatory) this.$router.replace('/interventions');
                }, 2000);
            } catch (error) {
                this.errorMessage = error?.message || 'Impossible de mettre à jour le profil';
            } finally {
                this.saving = false;
            }
        },

        openSignaturePad() {
            this.showSignaturePad = true;
            this.$nextTick(() => this.initCanvas());
        },

        initCanvas() {
            const canvas = this.$refs.signatureCanvas;
            if (!canvas) return;
            canvas.width  = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
            this._ctx = canvas.getContext('2d');
            this._ctx.strokeStyle = '#1e293b';
            this._ctx.lineWidth   = 2.5;
            this._ctx.lineCap     = 'round';
            this._ctx.lineJoin    = 'round';
        },

        startDraw(e) {
            this._drawing = true;
            this._ctx?.beginPath();
            this._ctx?.moveTo(e.offsetX, e.offsetY);
        },

        draw(e) {
            if (!this._drawing || !this._ctx) return;
            this._ctx.lineTo(e.offsetX, e.offsetY);
            this._ctx.stroke();
        },

        stopDraw() { this._drawing = false; },

        startDrawTouch(e) {
            const rect  = this.$refs.signatureCanvas.getBoundingClientRect();
            const touch = e.touches[0];
            this._drawing = true;
            this._ctx?.beginPath();
            this._ctx?.moveTo(touch.clientX - rect.left, touch.clientY - rect.top);
        },

        drawTouch(e) {
            if (!this._drawing || !this._ctx) return;
            const rect  = this.$refs.signatureCanvas.getBoundingClientRect();
            const touch = e.touches[0];
            this._ctx.lineTo(touch.clientX - rect.left, touch.clientY - rect.top);
            this._ctx.stroke();
        },

        clearCanvas() {
            const canvas = this.$refs.signatureCanvas;
            if (canvas && this._ctx) this._ctx.clearRect(0, 0, canvas.width, canvas.height);
        },

        async saveSignature() {
            const canvas = this.$refs.signatureCanvas;
            if (!canvas) return;
            const dataUrl = canvas.toDataURL('image/png');
            try {
                this.uploadingSignature = true;
                const url = await this.uploadBase64ToStorage(
                    dataUrl,
                    'technicien_signature',
                    `technicien_signature_${this.profile?.id}.png`
                );
                this.form.signature   = url;
                this.showSignaturePad = false;
            } catch {
                this.form.signature   = dataUrl;
                this.showSignaturePad = false;
            } finally {
                this.uploadingSignature = false;
            }
        },

        base64ToBlob(base64) {
            const arr   = base64.split(',');
            const mime  = arr[0].match(/:(.*?);/)[1];
            const bstr  = atob(arr[1]);
            let n       = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) u8arr[n] = bstr.charCodeAt(n);
            return new Blob([u8arr], { type: mime });
        },

        async uploadBase64ToStorage(base64, key, filename = 'file.png') {
            const blob     = this.base64ToBlob(base64);
            const formData = new FormData();
            formData.append('key_name', key);
            formData.append('file', blob, filename);
            const response = await axios.post(
                'https://storage.termoflow.fr/upload.php',
                formData,
                { headers: { Authorization: 'Bearer PRODt1ntH4nCMTfw5tGrXy0ZRgyz4Q', 'Content-Type': 'multipart/form-data' } }
            );
            if (!response.data?.success) throw new Error('Upload échoué');
            return response.data.url;
        },

        logout() {
            if (confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
                this.$router.push('/logout');
            }
        },
    },
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; }
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s; }
.modal-enter, .modal-leave-to { opacity: 0; }
.dropdown-enter-active, .dropdown-leave-active { transition: all 0.2s ease; }
.dropdown-enter, .dropdown-leave-to { opacity: 0; transform: translateY(-4px); }
</style>